import { Company } from '@nextforce/api'
import { makeStyles, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useCompaniesContext } from 'company/context'
import { useUserCompanies } from 'company/queries/user_companies'
import { SelectCompanyScreenProps } from 'home/stack/types/select_company'
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { FlatList, Text, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { CheckBox } from 'shared/components/check_box'
import { showHeaderButton } from 'shared/header/header_button'

export function SelectCompanyScreen({
  navigation,
  route,
}: SelectCompanyScreenProps) {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    null
  )
  const { theme } = useTheme()
  const { companies: contextCompanies } = useCompaniesContext()
  const { data: userCompanies } = useUserCompanies()

  const companies = useMemo(() => {
    if (userCompanies && userCompanies.length > 0) {
      return userCompanies
    }
    return contextCompanies ?? []
  }, [contextCompanies, userCompanies])

  const hasValidSelectedCompany = companies.some(
    company => company.id === selectedCompanyId
  )

  const styles = useStyles()

  const handleCompanySelect = useCallback((companyId: string) => {
    setSelectedCompanyId(companyId)
  }, [])

  const handleContinue = useCallback(() => {
    if (selectedCompanyId && hasValidSelectedCompany) {
      navigation.navigate('choose_members', {
        companyId: selectedCompanyId,
      })
    }
  }, [hasValidSelectedCompany, navigation, selectedCompanyId])

  useLayoutEffect(() => {
    showHeaderButton({
      navigation,
      theme,
      title: t('next'),
      onPress: handleContinue,
      position: 'right',
      disabled: !hasValidSelectedCompany || companies.length === 0,
    })
  }, [navigation, theme, handleContinue, hasValidSelectedCompany, companies.length])

  const renderCompanyItem = ({ item }: { item: Company }) => {
    const isSelected = selectedCompanyId === item.id
    return (
      <TouchableOpacity
        key={item.id}
        style={[
          styles.companyItem,
          isSelected && { backgroundColor: theme.colors.primary + '20' },
        ]}
        onPress={() => handleCompanySelect(item.id)}
      >
        <Text style={styles.companyName}>{item.name}</Text>
        <CheckBox
          checked={isSelected}
          onPress={() => handleCompanySelect(item.id)}
        />
      </TouchableOpacity>
    )
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <Text style={styles.description}>{t('selectCompanyDescription')}</Text>
        <FlatList
          data={companies}
          renderItem={renderCompanyItem}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContainer}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
          ListEmptyComponent={
            <Text style={styles.emptyText}>{t('noResults')}</Text>
          }
        />
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: 12,
  },
  description: {
    marginBottom: 16,
    opacity: 0.7,
    fontSize: 15,
    color: theme.colors?.black,
  },
  listContainer: {
    paddingBottom: 16,
  },
  companyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 8,
    marginVertical: 4,
  },
  companyName: {
    flex: 1,
    fontSize: 16,
    color: theme.colors?.black,
  },
  separator: {
    height: 1,
    backgroundColor: theme.colors.grey7,
  },
  emptyText: {
    textAlign: 'center',
    marginTop: 20,
    color: theme.colors?.grey2,
  },
}))
